import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { api } from '@/services/api'
import { cn } from '@/utils/cn'
import { GlobalErrorHandler } from '@/utils/errorRecovery'
import { BaseError, ErrorSeverity } from '@/utils/errors'
import { Activity, AlertTriangle, XCircle } from 'lucide-react'
import { useEffect, useState } from 'react'

interface ErrorStat {
  key: string
  count: number
}

interface CircuitBreakerState {
  name: string
  state: 'CLOSED' | 'OPEN' | 'HALF_OPEN' | 'DISABLED'
  failures: number
  lastFailureTime: number
  lastSuccessTime?: number
  threshold?: number
  resetTimeout?: number
  requestCount?: number
  averageResponseTime?: number
}

export function ErrorMonitor({ isOpen, onClose }: { isOpen?: boolean; onClose?: () => void }) {
  const [errorStats, setErrorStats] = useState<ErrorStat[]>([])
  const [circuitBreakers, setCircuitBreakers] = useState<Record<string, CircuitBreakerState>>({})
  const [lastError, setLastError] = useState<BaseError | null>(null)
  const [globalErrorHandler] = useState(() => new GlobalErrorHandler(setLastError))

  // Update stats periodically
  useEffect(() => {
    const updateStats = () => {
      setErrorStats(globalErrorHandler.getErrorStats())
      setCircuitBreakers(api.getCircuitBreakerStates())
    }

    updateStats()
    const interval = setInterval(updateStats, 2000)

    return () => clearInterval(interval)
  }, [globalErrorHandler])

  // Add keyboard shortcut to close (Escape key)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && onClose) {
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown)
      return () => document.removeEventListener('keydown', handleKeyDown)
    }
    
    // Return a no-op cleanup function when not open
    return () => {}
  }, [isOpen, onClose])

  // Don't render if not open
  if (!isOpen) {
    return null
  }

  const resetCircuitBreakers = () => {
    api.resetCircuitBreakers()
    setCircuitBreakers(api.getCircuitBreakerStates())
  }

  const getCircuitBreakerColor = (state: string) => {
    switch (state) {
      case 'OPEN':
        return 'text-red-500'
      case 'HALF_OPEN':
        return 'text-yellow-500'
      case 'CLOSED':
        return 'text-green-500'
      case 'DISABLED':
        return 'text-gray-500'
      default:
        return 'text-gray-500'
    }
  }

  const getSeverityColor = (severity: ErrorSeverity) => {
    switch (severity) {
      case ErrorSeverity.CRITICAL:
        return 'text-red-500'
      case ErrorSeverity.HIGH:
        return 'text-orange-500'
      case ErrorSeverity.MEDIUM:
        return 'text-yellow-500'
      case ErrorSeverity.LOW:
        return 'text-blue-500'
      default:
        return 'text-gray-500'
    }
  }

  return (
    <Card className="fixed bottom-20 right-4 w-96 max-h-[600px] overflow-hidden shadow-2xl z-40">
      <div className="p-4 border-b">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Activity className="w-5 h-5 text-primary" />
            <h3 className="font-semibold">Error Monitor</h3>
          </div>
          {onClose && (
            <Button
              size="sm"
              variant="ghost"
              onClick={onClose}
              className="hover:bg-destructive/20"
              title="Close (Esc)"
            >
              <XCircle className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>

      <div className="p-4 space-y-4 max-h-[500px] overflow-y-auto">
        {/* Circuit Breakers */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-medium">Circuit Breakers</h4>
            <Button
              size="sm"
              variant="outline"
              onClick={resetCircuitBreakers}
            >
              
              Reset All
            </Button>
          </div>
          <div className="space-y-2">
            {Object.entries(circuitBreakers).map(([name, state]) => (
              <div key={name} className="flex items-center justify-between p-2 bg-muted/50 rounded-md">
                <div className="flex items-center gap-2">
                  <div className={cn("w-2 h-2 rounded-full", {
                    'bg-green-500': state.state === 'CLOSED',
                    'bg-yellow-500': state.state === 'HALF_OPEN',
                    'bg-red-500': state.state === 'OPEN',
                    'bg-gray-400': state.state === 'DISABLED'
                  })} />
                  <span className="text-sm font-medium capitalize">{name}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className={getCircuitBreakerColor(state.state)}>
                    {state.state}
                  </Badge>
                  {state.failures > 0 && (
                    <span className="text-xs text-muted-foreground">
                      {state.failures} failures
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Error Stats */}
        {errorStats.length > 0 && (
          <div>
            <h4 className="text-sm font-medium mb-2">Recent Errors</h4>
            <div className="space-y-2">
              {errorStats.map((stat) => (
                <div key={stat.key} className="flex items-center justify-between p-2 bg-muted/50 rounded-md">
                  <span className="text-sm truncate flex-1">{stat.key.split('-')[0]}</span>
                  <Badge variant="destructive">{stat.count}</Badge>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Last Error */}
        {lastError && (
          <div>
            <h4 className="text-sm font-medium mb-2">Last Error</h4>
            <div className="p-3 bg-muted/50 rounded-md space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">{lastError.name}</span>
                <Badge className={cn("text-xs", getSeverityColor(lastError.severity))}>
                  {lastError.severity}
                </Badge>
              </div>
              <p className="text-xs text-muted-foreground">{lastError.message}</p>
              <p className="text-xs text-muted-foreground">
                {lastError.metadata.timestamp.toLocaleTimeString()}
              </p>
              {lastError.metadata.requestId && (
                <p className="text-xs font-mono text-muted-foreground">
                  Request: {lastError.metadata.requestId}
                </p>
              )}
            </div>
          </div>
        )}

        {!errorStats.length && !lastError && (
          <div className="text-center py-8 text-muted-foreground">
            <AlertTriangle className="w-12 h-12 mx-auto mb-2 opacity-20" />
            <p className="text-sm">No errors detected</p>
          </div>
        )}
      </div>
    </Card>
  )
}

// Development-only floating button to toggle monitor
export function ErrorMonitorToggle() {
  const [isOpen, setIsOpen] = useState(false)

  if (import.meta.env.PROD) {
    return null
  }

  return (
    <>
      <Button
        size="sm"
        variant="outline"
        className="fixed bottom-4 right-4 z-50 shadow-lg"
        onClick={() => setIsOpen(!isOpen)}
      >
        <Activity className="w-4 h-4" />
      </Button>
      <ErrorMonitor isOpen={isOpen} onClose={() => setIsOpen(false)} />
    </>
  )
}